/**
 * Fixed 9-color palette for shop tags. Mirrors the TAG_COLORS array in
 * worker/routes/tags.ts — both files validate against the same token
 * set. Extending requires changing both; intentionally not a one-place
 * import because the worker bundle shouldn't pull anything from the
 * React tree.
 *
 * Each token maps to chip Tailwind classes (default bg/text/border)
 * AND to a 16x16 swatch class for the color-picker grid. Class lists
 * are written out verbatim so Tailwind's JIT detects them at build
 * time — never compose with string concat (`bg-${color}-100`) or the
 * classes get tree-shaken.
 *
 * Values are OKLCH, harmonized with the DESIGN.md §1 ramp (washes
 * L≈0.96, text L 0.42–0.45, hairlines L≈0.86) and verified in-gamut
 * with text-on-wash contrast ≥ 6.8:1 (floor 4.5:1). Per §2 rule 3 the
 * `emerald` token is tuned to TEAL (hue 185) so a customer tag never
 * impersonates receipt green (`saved`, hue 152); `gray` sits on the §1
 * neutral hue (155) and `slate` is the cool blue-gray counterpart.
 * Token NAMES are wire format (worker-validated) — retune values only.
 */

export const TAG_COLORS = [
  'slate',
  'brand',
  'emerald',
  'amber',
  'red',
  'blue',
  'violet',
  'pink',
  'gray',
] as const

export type TagColor = (typeof TAG_COLORS)[number]

export function isTagColor(value: unknown): value is TagColor {
  return (
    typeof value === 'string' && TAG_COLORS.includes(value as TagColor)
  )
}

// Chip styles applied to a single tag pill. `chip` is the full
// composite class for the pill body; `dot` is a tiny inline indicator
// used inside the color-picker rows and select menus. Chips carry no
// hover styles (§4.9 status chips are display surfaces; interactive
// affordances live on the host control).
type ChipStyle = {
  chip: string
  dot: string
}

const CHIP_STYLES: Record<TagColor, ChipStyle> = {
  slate: {
    chip: 'bg-[oklch(0.962_0.008_250)] text-[oklch(0.42_0.03_250)] border border-[oklch(0.87_0.02_250)]',
    dot: 'bg-[oklch(0.62_0.04_250)]',
  },
  brand: {
    chip: 'bg-[oklch(0.958_0.018_262)] text-[oklch(0.42_0.09_262)] border border-[oklch(0.86_0.045_262)]',
    dot: 'bg-[oklch(0.6_0.1_262)]',
  },
  emerald: {
    chip: 'bg-[oklch(0.958_0.03_185)] text-[oklch(0.42_0.07_185)] border border-[oklch(0.85_0.055_185)]',
    dot: 'bg-[oklch(0.62_0.1_185)]',
  },
  amber: {
    chip: 'bg-[oklch(0.965_0.03_80)] text-[oklch(0.45_0.092_72)] border border-[oklch(0.86_0.075_82)]',
    dot: 'bg-[oklch(0.72_0.14_78)]',
  },
  red: {
    chip: 'bg-[oklch(0.96_0.018_25)] text-[oklch(0.45_0.14_27)] border border-[oklch(0.86_0.055_25)]',
    dot: 'bg-[oklch(0.6_0.16_27)]',
  },
  blue: {
    chip: 'bg-[oklch(0.96_0.02_237)] text-[oklch(0.44_0.105_245)] border border-[oklch(0.86_0.05_240)]',
    dot: 'bg-[oklch(0.6_0.13_242)]',
  },
  violet: {
    chip: 'bg-[oklch(0.96_0.022_300)] text-[oklch(0.44_0.12_300)] border border-[oklch(0.87_0.05_300)]',
    dot: 'bg-[oklch(0.62_0.12_300)]',
  },
  pink: {
    chip: 'bg-[oklch(0.962_0.018_350)] text-[oklch(0.45_0.13_355)] border border-[oklch(0.87_0.05_352)]',
    dot: 'bg-[oklch(0.64_0.15_355)]',
  },
  gray: {
    chip: 'bg-[oklch(0.965_0.004_155)] text-[oklch(0.42_0.018_160)] border border-[oklch(0.87_0.008_155)]',
    dot: 'bg-[oklch(0.62_0.015_158)]',
  },
}

export function chipClass(color: string | null | undefined): string {
  const c = isTagColor(color) ? color : 'slate'
  return CHIP_STYLES[c].chip
}

export function chipDotClass(color: string | null | undefined): string {
  const c = isTagColor(color) ? color : 'slate'
  return CHIP_STYLES[c].dot
}

export const DEFAULT_TAG_COLOR: TagColor = 'slate'
